import { Box, Button, Typography, SxProps, Theme } from '@mui/material';

import { RelevanceBadge } from './RelevanceBadge';
import { RelevanceRating } from '../types';

/**
 * Props for the RelevanceFilters component
 */
export interface RelevanceFiltersProps {
  /** Set of currently visible relevance ratings */
  visibleRatings: Set<RelevanceRating>;
  /** Callback when a rating filter is toggled */
  onToggle: (rating: RelevanceRating) => void;
  /** Callback when "Clear all" is clicked */
  onClearAll?: () => void;
}

const RATINGS: RelevanceRating[] = ['High', 'Medium', 'Low'];

/**
 * RelevanceFilters component - Renders toggle badges for filtering relevance ratings
 *
 * Displays "Show:" label followed by three toggle badges (High, Medium, Low)
 * with white backgrounds and colored dot indicators. Active badges have full opacity,
 * inactive badges have reduced opacity. Includes "Clear all" button with refresh icon.
 *
 * Uses the shared RelevanceBadge component for consistent badge styling.
 *
 * @example
 * ```tsx
 * const [visibleRatings, setVisibleRatings] = useState(new Set(['High', 'Medium', 'Low']));
 *
 * const handleToggle = (rating: RelevanceRating) => {
 *   const newRatings = new Set(visibleRatings);
 *   if (newRatings.has(rating)) {
 *     newRatings.delete(rating);
 *   } else {
 *     newRatings.add(rating);
 *   }
 *   setVisibleRatings(newRatings);
 * };
 *
 * <RelevanceFilters
 *   visibleRatings={visibleRatings}
 *   onToggle={handleToggle}
 *   onClearAll={() => setVisibleRatings(new Set(['High', 'Medium', 'Low']))}
 * />
 * ```
 */
export const RelevanceFilters = ({
  visibleRatings,
  onToggle,
  onClearAll,
}: RelevanceFiltersProps) => {
  return (
    <Box sx={containerSx}>
      <Box sx={badgesContainerSx}>
        <Box sx={badgesRowSx}>
          {RATINGS.map((rating) => (
            <RelevanceBadge
              key={rating}
              id={`majors-guide-relevance-filter-${rating.toLowerCase()}`}
              level={rating}
              interactive
              isActive={visibleRatings.has(rating)}
              onClick={() => onToggle(rating)}
            />
          ))}
        </Box>
      </Box>

      <Typography sx={separatorSx}>|</Typography>

      {onClearAll && (
        <Button
          id="relevance-filters-clear-button"
          variant="contained"
          size="sm"
          hierarchy="Secondary gray"
          onClick={onClearAll}
          sx={clearButtonSx}
        >
          Clear
        </Button>
      )}
    </Box>
  );
};

// Styles

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs?.spacing?.['spacing-md'] ?? '0.5rem',
});

const badgesContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs?.spacing?.['spacing-md'] ?? '0.5rem',
});

const badgesRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs?.spacing?.['spacing-md'] ?? '12px',
});

const separatorSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs?.colors?.['Colors/Border/border-secondary'] ?? '#d1d5db',
  fontSize: '1rem',
  lineHeight: 1,
});

const clearButtonSx: SxProps<Theme> = ({ backrs }) => ({
  whiteSpace: 'nowrap',
  minHeight: 'auto',
  py: backrs?.spacing?.['spacing-xs'] ?? '4px',
  px: backrs?.spacing?.['spacing-md'] ?? '10px',
});
